import { scaleLinear, scaleOrdinal } from 'd3-scale';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Fragment, useState } from 'react';
import type { Visualization } from '../types';
import { SvgWithFont } from '../utils/SvgWithFont';
import { getContrastingTextColor } from '../utils/colors';
import { Dot } from './Dot';
import { PlaceholderBackground } from './PlaceholderBackground';
import { RatingGridLegend } from './RatingGridLegend';
import { HorizontalAxis } from './HorizontalAxis';
import { VerticalAxis } from './VerticalAxis';
import { AXIS_LABEL_PADDING, AXIS_LABEL_SIZE } from './constants';

const MIN_DOT_RADIUS = 16;
const SVG_SIZE_TO_DOT_SIZE_RATIO = 1 / 30;

interface RatingGridResultData {
  x: number;
  y: number;
}

export interface RatingGridData {
  id: string;
  score: {
    average: RatingGridResultData;
    responses: RatingGridResultData[];
  };
}

interface RatingGridStaticOptions {
  id: string;
  label: string;
  dotNumber: string;
}

function hasUrl(
  image: { url: string } | { placeholderImageColor: string },
): image is { url: string } {
  return 'url' in image;
}

export const RatingGrid = ({
  width,
  height,
  fontFamily,
  image,
  data,
  colors,
  staticOptions,
  disableAnimations,
  keyAccessor = (d: RatingGridData | RatingGridStaticOptions) => d.id,
  labelAccessor = (d: RatingGridStaticOptions) => d.dotNumber,
  valueAccessor = (d: RatingGridData) => d.score.average,
  range,
  backgroundColor,
  horizontalAxisLabel,
  verticalAxisLabel,
}: Visualization<
  RatingGridData,
  RatingGridStaticOptions,
  RatingGridResultData
> & {
  backgroundColor: string;
  image: { url: string } | { placeholderImageColor: string };
  range: { min: number; max: number };
  horizontalAxisLabel: string;
  verticalAxisLabel: string;
}) => {
  const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);

  const discreteValuesBasedOnId = staticOptions.map(keyAccessor);

  const fillColors = scaleOrdinal(colors).domain(discreteValuesBasedOnId);

  const axisLabelSize = AXIS_LABEL_SIZE + AXIS_LABEL_PADDING;
  const visualizationWidth = width > 1000 ? width * 0.55 : width * 0.65;
  const maxSvgWidth = visualizationWidth - axisLabelSize;
  const marginForDotHeightRatio = 1 - SVG_SIZE_TO_DOT_SIZE_RATIO;
  const maxSvgHeight = (height - axisLabelSize) * marginForDotHeightRatio;
  const svgSize = Math.min(maxSvgWidth, maxSvgHeight);

  const visualizationLegendGap = Math.min(80, width * 0.05);
  const legendWidth = width - axisLabelSize - svgSize - visualizationLegendGap;

  const scaleX = scaleLinear()
    .domain([range.min, range.max])
    .range([0, svgSize]);
  const scaleY = scaleLinear()
    .domain([range.min, range.max])
    .range([svgSize, 0]);

  if (svgSize <= 0 || svgSize <= 0) {
    return null;
  }

  return (
    <Box
      width="100%"
      height="100%"
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
    >
      <VerticalAxis text={verticalAxisLabel} height={svgSize} />
      <Box
        width={svgSize}
        height={height}
        mr={visualizationLegendGap}
        justifyContent="center"
      >
        <SvgWithFont
          width={svgSize}
          height={svgSize}
          fontFamily={fontFamily}
          style={{ overflow: 'visible' }}
          data-testid="rating-grid-svg"
        >
          {hasUrl(image) ? (
            <image href={image.url} width={svgSize} height={svgSize} />
          ) : (
            <PlaceholderBackground
              width={svgSize}
              height={svgSize}
              color={image.placeholderImageColor}
            />
          )}
          {data.map((d) => {
            const x = scaleX(valueAccessor(d).x);
            const y = scaleY(valueAccessor(d).y);
            const key = keyAccessor(d);

            const staticOption = staticOptions.find(
              (option) => keyAccessor(option) === key,
            ) ?? { id: '', label: '', dotNumber: '' };
            const label = labelAccessor(staticOption);
            const color = fillColors(keyAccessor(staticOption));
            const textColor = getContrastingTextColor(color);

            const shouldDisplayDotResults = hoveredItemKey === d.id;
            const shouldHideDot =
              hoveredItemKey !== null && hoveredItemKey !== d.id;

            if (shouldHideDot) {
              return null;
            }

            const dotSize = Math.max(
              svgSize * SVG_SIZE_TO_DOT_SIZE_RATIO,
              MIN_DOT_RADIUS,
            );

            return (
              <Fragment key={key}>
                {shouldDisplayDotResults &&
                  d.score.responses.map((response, index) => {
                    const responseX = scaleX(response.x);
                    const responseY = scaleY(response.y);
                    return (
                      <Dot
                        key={index}
                        x={responseX}
                        y={responseY}
                        label=""
                        radius={12}
                        color={color}
                        borderColor={backgroundColor}
                        textColor={textColor}
                        disableAnimations={disableAnimations}
                      />
                    );
                  })}
                <Dot
                  x={x}
                  y={y}
                  label={label}
                  radius={dotSize}
                  color={color}
                  borderColor={backgroundColor}
                  textColor={textColor}
                  disableAnimations={disableAnimations}
                  onMouseEnter={() => setHoveredItemKey(d.id)}
                  onMouseLeave={() => setHoveredItemKey(null)}
                />
              </Fragment>
            );
          })}
        </SvgWithFont>
        <HorizontalAxis text={horizontalAxisLabel} />
      </Box>
      <RatingGridLegend
        items={staticOptions.map((staticOption) => ({
          id: staticOption.id,
          label: staticOption.label,
          dotNumber: staticOption.dotNumber,
          color: fillColors(keyAccessor(staticOption)),
        }))}
        onItemHover={setHoveredItemKey}
        itemKeyAccessor={(item) => item.id}
        maxHeight={height}
        minWidth={svgSize}
        maxWidth={legendWidth}
        hoveredItemKey={hoveredItemKey}
      />
    </Box>
  );
};
